import AscenderLogo from "@/assets/projeto-ascender-logo.png";
import MissoesCaatingaMasonryGallery from "@/components/sections/MissoesCaatingaMasonryGallery";
import AscenderMusicSpotlight from "@/components/sections/AscenderMusicSpotlight";
import AscenderCharityWorks from "@/components/sections/AscenderCharityWorks";
import AscenderCharityServicesOffered from "@/components/sections/AscenderCharityServicesOffered";
import { ChevronDown } from "lucide-react";

import CasaCaridade1 from "@/assets/ascender-casas-1.jpg";
import CasaCaridade2 from "@/assets/ascender-casas-2.png";
import CasaCaridade3 from "@/assets/ascender-casas-3.jpg";
import CasaCaridade5 from "@/assets/ascender-casas-5.jpg";

import AscenderInformatica1 from "@/assets/ascender-informatica-1.jpg";
import AscenderInformatica2 from "@/assets/ascender-informatica-2.jpg";

const ProjetoAscender = () => {
  const casasItems = [
    {
      id: "ascender-casas-1",
      img: CasaCaridade1,
      height: 900,
      alt: "Construção de casa: parede de tijolos com janela em acabamento",
    },
    {
      id: "ascender-casas-2",
      img: CasaCaridade2,
      height: 820,
      alt: "Voluntário preparando massa para construção de casa",
    },
    {
      id: "ascender-casas-3",
      img: CasaCaridade3,
      height: 780,
      alt: "Equipe trabalhando na obra com carrinho de mão e ferramentas",
    },
    {
      id: "ascender-casas-5",
      img: CasaCaridade5,
      height: 760,
      alt: "Voluntários trabalhando no telhado durante a reforma da casa",
    },
  ];

  const informaticaItems = [
    {
      id: "ascender-info-1",
      img: AscenderInformatica1,
      height: 900,
      alt: "Curso de informática: alunos utilizando computadores durante a aula",
    },
    {
      id: "ascender-info-2",
      img: AscenderInformatica2,
      height: 900,
      alt: "Curso de informática: turma em atividade prática nos computadores",
    },
  ];




  return (
    <section className="section-shell">
      <header className="mb-10 grid gap-6 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] md:items-center">
        <div className="space-y-3 max-w-2xl">
          <p className="section-title">Projeto Ascender</p>
          <h1 className="section-heading">Cuidando hoje para reconstruir o amanhã.</h1>
          <p className="section-subtitle">
            O Projeto Ascender é a frente de ação social do IESSA que se aproxima de pessoas em situação
            de vulnerabilidade para suprir necessidades imediatas e caminhar junto em um processo de restauração,
            dignidade e reinserção na sociedade.
          </p>
        </div>

        <div className="flex items-center justify-center">
          <div className="relative flex items-center justify-center rounded-3xl bg-primary/5 px-6 py-5 shadow-inner md:px-8 md:py-7">
            <img
              src={AscenderLogo}
              alt="Logotipo do Projeto Ascender, com uma pomba representando esperança e nova vida"
              className="max-h-32 w-auto object-contain md:max-h-40"
              loading="lazy"
              width={160}
              height={160}
            />
          </div>
        </div>
      </header>

      <section className="grid gap-10 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] md:items-start">
        <article className="space-y-5">
          <h2 className="text-xl font-semibold text-foreground">Sobre o projeto</h2>
          <p className="text-sm text-muted-foreground">
            Mais do que doar cestas ou realizar ações pontuais, o Ascender busca construir uma jornada de cuidado
            completo. Começamos ouvindo histórias, identificando necessidades e, a partir disso, traçamos caminhos
            possíveis para que cada pessoa possa sair da situação de vulnerabilidade em que se encontra.
          </p>
          <p className="text-sm text-muted-foreground">
            Cada visita é guiada por amor, empatia e responsabilidade, com apoio de voluntários preparados para agir
            com sensibilidade e excelência cristã.
          </p>

          <div className="grid gap-4 md:grid-cols-3">
            <HighlightCard
              title="Acolher"
              description="Nos aproximamos com escuta atenta, oração e presença que gera confiança."
            />
            <HighlightCard
              title="Cuidar"
              description="Suprimos necessidades básicas imediatas para aliviar a dor do agora."
            />
            <HighlightCard
              title="Acompanhar"
              description="Caminhamos juntos, conectando a redes de apoio e oportunidades reais."
            />
          </div>
        </article>

        <aside className="card-ambient p-6 space-y-4">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Como funciona o acompanhamento</p>
          <ol className="space-y-3 text-sm text-muted-foreground list-decimal list-inside">
            <li>Identificação da família ou indivíduo em situação de vulnerabilidade.</li>
            <li>Visita inicial com levantamento das necessidades mais urgentes.</li>
            <li>Suprimento das necessidades imediatas (alimentos, itens básicos, orientação e oração).</li>
            <li>Construção de um plano de acompanhamento personalizado.</li>
            <li>Encaminhamento para mercado de trabalho, cursos e oportunidades de renda.</li>
            <li>Quando necessário, direcionamento a órgãos e serviços especializados, como CAPS ou clínicas de reabilitação.</li>
          </ol>
        </aside>
      </section>

      <section className="mt-12 space-y-6">
        <h2 className="text-xl font-semibold text-foreground">Ações práticas do Projeto Ascender</h2>
        <p className="max-w-3xl text-sm text-muted-foreground">
          O Ascender atua sempre unindo compaixão e responsabilidade, cuidando do corpo, da alma e das relações.
          Conheça algumas das principais frentes de ação:
        </p>

        <div className="grid gap-5 md:grid-cols-3">
          <ActionCard
            title="Visitas domiciliares"
            description="Equipes que vão até a casa de pessoas e famílias para ouvir, orar e suprir necessidades imediatas."
          />
          <ActionCard
            title="Encaminhamento profissional"
            description="Apoio na busca por emprego, orientação para entrevistas e conexão com oportunidades de trabalho."
          />
          <ActionCard
            title="Rede de cuidado integral"
            description="Parcerias com CAPS, clínicas de reabilitação e outros órgãos que ampliam o cuidado e o tratamento."
          />
        </div>
      </section>

      <AscenderCharityServicesOffered />

      <div className="mt-10 space-y-3 text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
          Confira nossa galeria abaixo
        </p>
        <h2 className="mx-auto max-w-3xl text-3xl font-semibold leading-tight text-foreground md:text-4xl">
          Veja de perto como cada ação se transforma em cuidado.
        </h2>
        <div className="pt-2">
          <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full border border-border/60 bg-card/60 shadow-sm">
            <ChevronDown
              className="h-5 w-5 text-muted-foreground animate-[bounce_1.8s_ease-in-out_infinite]"
              aria-hidden="true"
            />
          </div>
          <span className="sr-only">Role para ver a galeria</span>
        </div>
      </div>

      <section className="mt-12 space-y-6">
        <header className="space-y-2">
          <h2 className="text-xl font-semibold text-foreground">Galeria: construção de casas</h2>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Registros do serviço de caridade que apoia famílias com reforma e construção de moradias, promovendo dignidade
            e recomeço.
          </p>
        </header>
        <MissoesCaatingaMasonryGallery items={casasItems} />
      </section>

      <section className="mt-12 space-y-6">
        <header className="space-y-2">
          <h2 className="text-xl font-semibold text-foreground">Galeria: curso de informática</h2>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Registros das turmas e atividades do curso de informática.
          </p>
        </header>
        <MissoesCaatingaMasonryGallery items={informaticaItems} />
      </section>

      {/* Novas seções visuais (mock) */}
      <AscenderMusicSpotlight />
      <AscenderCharityWorks />

      <section className="mt-12 grid gap-8 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] md:items-center">
        <article className="space-y-4">
          <h2 className="text-xl font-semibold text-foreground">Como você pode se envolver</h2>
          <p className="text-sm text-muted-foreground">
            O Projeto Ascender acontece graças a uma rede de pessoas generosas que oram, contribuem e se dispõem a
            servir. Você pode fazer parte desse movimento de esperança.
          </p>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>• Orando regularmente pelas famílias atendidas e pelos voluntários.</li>
            <li>• Contribuindo financeiramente para manter as ações e suprimentos emergenciais.</li>
            <li>• Participando das equipes que realizam visitas e acompanhamentos.</li>
            <li>• Indicando profissionais e oportunidades de trabalho para quem está recomeçando.</li>
          </ul>
        </article>

        <aside className="card-ambient p-6 space-y-3">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Quer saber mais?</p>
          <p className="text-sm text-muted-foreground">
            Para receber informações sobre o Projeto Ascender, tirar dúvidas ou se voluntariar, entre em contato com a
            equipe do IESSA pelo WhatsApp ou pelos canais de comunicação disponíveis na página de contato.
          </p>
        </aside>
      </section>
    </section>
  );
};

interface InfoCardProps {
  title: string;
  description: string;
}

const HighlightCard = ({ title, description }: InfoCardProps) => (
  <div className="card-ambient p-4">
    <p className="text-sm font-semibold text-foreground">{title}</p>
    <p className="mt-1 text-xs text-muted-foreground">{description}</p>
  </div>
);

const ActionCard = ({ title, description }: InfoCardProps) => (
  <article className="card-ambient p-5">
    <h3 className="mb-2 text-sm font-semibold text-foreground">{title}</h3>
    <p className="text-sm text-muted-foreground">{description}</p>
  </article>
);

export default ProjetoAscender;
